import { lazy, Suspense, useEffect, useState } from 'react'
import { Link, Route, Routes, useLocation } from 'react-router-dom'
import { ShoppingBag } from 'lucide-react'
import { Badge, Icone, LogoMark, Skeleton } from '@du/ui'
import { FolhaProduto } from './componentes/FolhaProduto.js'
import { api, BASE_API, type Bairro, type Categoria, type Loja, type Produto } from './lib/api.js'
import { useCarrinho } from './lib/carrinho.js'
import { capturarOrigem, capturarZap, registrarAcesso, type ContextoZap } from './lib/origem.js'
import { Acompanhar } from './paginas/Acompanhar.js'
import { Carrinho } from './paginas/Carrinho.js'
import { Checkout } from './paginas/Checkout.js'
import { Home } from './paginas/Home.js'
import { Privacidade } from './paginas/Privacidade.js'

const MinhaConta = lazy(() =>
  import('./paginas/MinhaConta.js').then((m) => ({ default: m.MinhaConta })),
)

export function App() {
  const [loja, setLoja] = useState<Loja | null>(null)
  const [categorias, setCategorias] = useState<Categoria[]>([])
  const [bairros, setBairros] = useState<Bairro[]>([])
  const [carregando, setCarregando] = useState(true)
  const [aberto, setAberto] = useState<Produto | null>(null)
  const [zap, setZap] = useState<ContextoZap | null>(null)
  const { totalItens } = useCarrinho()
  const local = useLocation()

  useEffect(() => {
    // Captura a origem ANTES de qualquer navegação do router: o caminho /cd
    // precisa ser lido e limpo antes de virar rota desconhecida.
    const origem = capturarOrigem()
    void registrarAcesso(BASE_API, origem)
    // Link do WhatsApp: troca o token pelos dados ANTES de o checkout abrir,
    // senão a pessoa digita o que a loja já sabe.
    void capturarZap(BASE_API).then(setZap)

    void (async () => {
      try {
        const [l, c, b] = await Promise.all([
          api<Loja>('/v1/publico/loja'),
          api<Categoria[]>('/v1/publico/catalogo'),
          api<Bairro[]>('/v1/publico/bairros'),
        ])
        setLoja(l)
        setCategorias(c)
        setBairros(b)
      } finally {
        setCarregando(false)
      }
    })()
  }, [])

  // Rolagem ao topo a cada tela: sem isto, entrar no checkout começa no meio.
  // Corpo em BLOCO, não conciso: `() => window.scrollTo(0, 0)` devolve o
  // retorno da chamada, e o React trata qualquer retorno não-função como
  // função de limpeza — derrubando a árvore inteira com "destroy is not a
  // function". Tela em branco, sem nada no typecheck.
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [local.pathname])

  const noCardapio = local.pathname === '/'

  return (
    <div className="cardapio">
      {/* Marca fixa no topo, em TODA tela. Antes o logo só existia na Home e
          no acompanhamento: no carrinho e no checkout — justo onde a pessoa
          decide gastar dinheiro — a marca sumia e a página virava um
          formulário anônimo. Clicável porque todo logo no topo é. */}
      <Link to="/" className="marca-topo" aria-label="Voltar ao cardápio">
        <LogoMark largura={168} />
      </Link>

      <Routes>
        <Route
          path="/"
          element={
            <Home loja={loja} categorias={categorias} carregando={carregando} aoEscolher={setAberto} />
          }
        />
        <Route path="/carrinho" element={<Carrinho />} />
        <Route path="/checkout" element={<Checkout loja={loja} bairros={bairros} zap={zap} />} />
        <Route path="/pedido/:codigo" element={<Acompanhar loja={loja} />} />
        <Route path="/privacidade" element={<Privacidade loja={loja} />} />
        {/*
          Lazy: a conta é a primeira tela do app que a maioria dos visitantes
          nunca vai abrir, e o orçamento de bundle existe por causa do tempo
          de carregamento no 4G.
        */}
        <Route
          path="/minha-conta"
          element={
            <Suspense fallback={<div className="pagina"><Skeleton altura={260} /></div>}>
              <MinhaConta categorias={categorias} />
            </Suspense>
          }
        />
        <Route
          path="*"
          element={
            <Home loja={loja} categorias={categorias} carregando={carregando} aoEscolher={setAberto} />
          }
        />
      </Routes>

      <FolhaProduto produto={aberto} aoFechar={() => setAberto(null)} />

      {/* Barra do carrinho: fixa no rodapé e só quando há item e estamos no
          cardápio. No celular é o polegar que alcança essa faixa. */}
      {totalItens > 0 && noCardapio ? (
        <Link to="/carrinho" className="barra-carrinho">
          <Icone de={ShoppingBag} tamanho={22} cor="preto" />
          <span>Ver carrinho</span>
          <Badge tom="destaque">{totalItens}</Badge>
        </Link>
      ) : null}
    </div>
  )
}
